/**
 * Camera utility for Native Browser API camera operations.
 * Replaces react-webcam with direct navigator.mediaDevices.getUserMedia usage.
 */

export interface CameraConstraints {
  height: { ideal: number };
  width: { ideal: number };
  facingMode: 'environment' | 'user';
}

export interface CameraInterface {
  stream: MediaStream | null;
  videoElement: HTMLVideoElement | null;
  initialize: (videoElement: HTMLVideoElement, constraints: CameraConstraints) => Promise<void>;
  takePhoto: (targetAspect?: number, rotate?: boolean, quality?: number) => Promise<{ file: File; dataUrl: string } | null>;
  close: () => void;
}

export interface CropRectangle {
  cropX: number;
  cropY: number;
  cropWidth: number;
  cropHeight: number;
}

/**
 * Calculate the centered source rectangle that would be visible when the source
 * (video/image) is displayed inside a container of the given `targetAspect`
 * using CSS `object-cover` semantics.
 *
 * - If the source is wider than the target aspect, its sides are cropped.
 * - If the source is taller than the target aspect, its top/bottom are cropped.
 *
 * The on-screen red capture frame is a visual guide *inside* this area at 90%
 * height; the capture itself uses this full 3:2 visible rectangle so
 * "what you see is what you get".
 */
export const calculateCropRectangle = (videoWidth: number, videoHeight: number, targetAspect: number = 3 / 2): CropRectangle => {
  const currentAspectRatio = videoWidth / videoHeight;
  let cropWidth = videoWidth;
  let cropHeight = videoHeight;
  let cropX = 0;
  let cropY = 0;

  if (currentAspectRatio > targetAspect) {
    // Video is wider than target: crop the width
    cropWidth = videoHeight * targetAspect;
    cropX = (videoWidth - cropWidth) / 2;
  } else if (currentAspectRatio < targetAspect) {
    // Video is taller than target: crop the height
    cropHeight = videoWidth / targetAspect;
    cropY = (videoHeight - cropHeight) / 2;
  }

  return { cropX, cropY, cropWidth, cropHeight };
};

const DEFAULT_CONSTRAINTS: CameraConstraints = {
  height: { ideal: 2988 },
  width: { ideal: 5312 },
  facingMode: 'environment',
};

/**
 * Creates a camera interface for managing camera stream and photo capture.
 */
export const createCameraInterface = (): CameraInterface => {
  let stream: MediaStream | null = null;
  let videoElement: HTMLVideoElement | null = null;

  /**
   * Initialize camera stream and attach to video element.
   */
  const initialize = async (video: HTMLVideoElement, constraints: CameraConstraints = DEFAULT_CONSTRAINTS): Promise<void> => {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('NotSupportedError: Camera API not supported');
    }
    try {
      try {
        // The first choice is back camera, but new Tablet(Dell or Lenovo) require "exact" to use back camera
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: constraints.width,
            height: constraints.height,
            facingMode: { exact: constraints.facingMode },
          },
          audio: false,
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: constraints.width,
            height: constraints.height,
            facingMode: constraints.facingMode,
          },
          audio: false,
        });
      }

      videoElement = video;

      // Wait for video to be ready
      await new Promise<void>((resolve, reject) => {
        const cleanup = () => {
          video.onloadedmetadata = null;
          video.onerror = null;
        };
        const handleMetadataLoaded = () => {
          cleanup();
          video.play().then(resolve).catch(reject);
        };
        const handleError = () => {
          cleanup();
          reject(new Error('Failed to load video'));
        };

        video.onloadedmetadata = handleMetadataLoaded;
        video.onerror = handleError;
        video.srcObject = stream;

        if (video.readyState >= 1) {
          handleMetadataLoaded();
        }
      });
    } catch (error) {
      close();
      throw error;
    }
  };

  /**
   * Capture photo from video stream using Canvas API.
   * Returns full resolution JPEG image.
   * @param quality - JPEG quality from 0 to 1. Default is 0.95.
   */
  const takePhoto = async (
    targetAspect?: number,
    rotate: boolean = false,
    quality: number = 0.98,
  ): Promise<{ file: File; dataUrl: string } | null> => {
    if (!videoElement || !stream) {
      return null;
    }

    const canvas = document.createElement('canvas');
    const videoWidth = videoElement.videoWidth;
    const videoHeight = videoElement.videoHeight;

    const { cropX, cropY, cropWidth, cropHeight } = calculateCropRectangle(videoWidth, videoHeight, targetAspect);

    // When rotating 90° clockwise, swap canvas width/height
    canvas.width = rotate ? cropHeight : cropWidth;
    canvas.height = rotate ? cropWidth : cropHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    if (rotate) {
      // Rotate 90 degrees clockwise: translate to right edge, then rotate
      ctx.translate(canvas.width, 0);
      ctx.rotate(Math.PI / 2);
    }

    ctx.drawImage(videoElement, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to capture image'));
            return;
          }

          const file = new File([blob], 'camera.jpg', {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });

          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve({ file, dataUrl });
        },
        'image/jpeg',
        quality,
      );
    });
  };

  /**
   * Close camera stream and release resources.
   */
  const close = (): void => {
    if (stream) {
      stream.getTracks().forEach((track) => {
        track.stop();
      });
      stream = null;
    }

    if (videoElement) {
      videoElement.srcObject = null;
      videoElement = null;
    }
  };

  return {
    get stream() {
      return stream;
    },
    get videoElement() {
      return videoElement;
    },
    initialize,
    takePhoto,
    close,
  };
};

export const DEFAULT_VIDEO_CONSTRAINTS: CameraConstraints = DEFAULT_CONSTRAINTS;

/**
 * Parse camera errors from Native Browser API (navigator.mediaDevices.getUserMedia)
 * and return user-friendly Japanese messages.
 */
export const parseCameraError = (error: string): string => {
  if (error.includes('NotAllowedError') || error.includes('Permission denied')) {
    return 'カメラの起動権限がありません';
  }

  if (error.includes('NotReadableError') || error.includes('Device in use')) {
    return '他のアプリケーションでカメラを使用中です';
  }

  if (error.includes('NotFoundError') || error.includes('Requested device not found')) {
    return 'カメラが見つかりません';
  }

  if (error.includes('NotSupportedError') || error.includes('Camera API not supported')) {
    return 'お使いのブラウザはカメラ機能をサポートしていません';
  }

  if (error.includes('OverconstrainedError')) {
    return 'カメラの設定が対応していません';
  }

  if (error.includes('AbortError')) {
    return 'カメラの起動が中断されました';
  }

  if (error.includes('SecurityError')) {
    return 'セキュリティエラー: HTTPSが必要です';
  }

  return error;
};
